import {
  BarChart3,
  DollarSign,
  Leaf,
  Truck,
  type LucideIcon,
} from "lucide-react";
import type { Permission } from "@/constants/permissions";
import { PERMISSIONS } from "@/constants/permissions";

export type PermissionRole = "internal" | "admin" | "standard";

export interface NavigationItem {
  /** Rota do item — usada também como identificador no menu. */
  id: string;
  label: string;
  icon: LucideIcon;
  /**
   * Filtro LEGADO por papel. Continua valendo para quem ainda não foi migrado
   * para os grupos do Keycloak — ver `filterNavigationGroups`.
   */
  roles: PermissionRole[];
  /**
   * Permissões do catálogo GR-637 que liberam este item. Basta UMA (`some`).
   *
   * O critério é "consegue fazer alguma coisa útil na tela": `/coletas` é uma
   * página de três abas, e quem tem direito a uma delas precisa do item no
   * menu. Permissão de download e de ação fica de fora — ela controla botão
   * dentro da tela, não a entrada.
   */
  permissions: Permission[];
  /** Nome do evento de clique de menu (GR-712). */
  analyticsLabel: string;
}

export interface NavigationGroup {
  id: string;
  /** Sem label o grupo é renderizado sem título. */
  label?: string;
  items: NavigationItem[];
}

/**
 * Contexto de acesso do usuário atual, vindo de `usePermissions`.
 *
 * Ausente (ou incompleto) = comportamento legado, idêntico ao de antes do
 * GR-637. É o que mantém o menu de pé para quem a migração ainda não alcançou.
 */
export interface NavigationAccess {
  /** Client roles efetivas do token — `resource_access["portal-gerador"].roles`. */
  permissions?: readonly string[];
  /** O usuário já pertence a algum grupo do catálogo. */
  isMigrated?: boolean;
  /** Usuário do realm `musa`. */
  isInternal?: boolean;
}

export const navigationGroups: NavigationGroup[] = [
  {
    id: "dashboards",
    label: "Dashboards",
    items: [
      {
        id: "/",
        label: "Operacional",
        icon: BarChart3,
        roles: ["internal", "admin", "standard"],
        permissions: [PERMISSIONS.VIEW_DASHBOARD],
        analyticsLabel: "menu_click_dashboard",
      },
      {
        id: "/indicadores-ambientais",
        label: "Indicador Ambiental",
        icon: Leaf,
        roles: ["internal", "admin", "standard"],
        // `view-environmental-certificates` fica FORA de propósito: hoje ela
        // sempre vem junto com a de indicadores (mesmo grupo `default`), então
        // somar não muda nada — e, se um dia for concedida sozinha, abriria a
        // tela inteira de indicadores para quem só deveria ver certificados. A
        // aba de Certificados tem gate próprio, por contrato.
        permissions: [PERMISSIONS.VIEW_ENVIRONMENTAL_INDICATORS],
        analyticsLabel: "menu_click_indicadores_ambientais",
      },
    ],
  },
  {
    id: "operacional",
    label: "Operacional",
    items: [
      {
        id: "/coletas",
        label: "Coletas",
        icon: Truck,
        roles: ["internal", "admin", "standard"],
        // Uma entrada para três abas (Próximas / Histórico / Sob demanda).
        // Quem tem direito a qualquer uma delas mantém o item.
        permissions: [
          PERMISSIONS.VIEW_UPCOMING_COLLECTS,
          PERMISSIONS.VIEW_COLLECTS_HISTORY,
          PERMISSIONS.VIEW_ON_DEMAND_COLLECTS,
        ],
        analyticsLabel: "menu_click_coletas",
      },
      {
        // `standard` entra junto de `admin`: o Financeiro é o único caminho do
        // gerador para consultar a medição publicada e contestá-la, e o canal de
        // abertura é exclusivo do portal. Sem o item de menu, o usuário padrão só
        // chegava à medição por link direto — a rota nunca foi restrita, apenas o
        // menu. Não amplia o que se vê: a listagem é escopada pelos geradores do
        // token.
        id: "/financeiro",
        label: "Financeiro",
        icon: DollarSign,
        // ATENÇÃO na virada: por papel, TODO usuário vê Financeiro (a
        // staging abriu para `standard` no GR-608). Por permissão, não —
        // quem concede `view-invoices` é o grupo `financial`, e nem o grupo
        // `admin` o concede. É a regra 1 do GR-561 (os dois eixos são
        // independentes) funcionando, mas significa que usuário migrado sem
        // o perfil Financeiro perde o item. A migração precisa atribuir
        // `financial` a quem de fato usa faturas.
        roles: ["internal", "admin", "standard"],
        permissions: [PERMISSIONS.VIEW_INVOICES],
        analyticsLabel: "menu_click_financeiro",
      },
    ],
  },
];

/** Itens exibidos enquanto o perfil ainda não tem `permission_role`. */
export const FALLBACK_ITEM_IDS: string[] = ["/", "/coletas"];

/**
 * Menu lateral filtrado para o usuário atual.
 *
 * Três regimes, nesta ordem — a ordem é o ponto:
 *
 * 1. INTERNO passa em tudo (GR-561, regra 10). Precisa vir primeiro porque o
 *    claim `groups` não existe no client do realm `musa`: `isMigrated` é
 *    estruturalmente `false` para interno, e checar migração antes o jogaria
 *    no fallback de dois itens. Interno também tem client roles de backoffice
 *    com o mesmo `clientId`, que não casam com nenhuma permissão de tela.
 *
 * 2. MIGRADO filtra por permissão do catálogo. É o regime novo.
 *
 * 3. NÃO MIGRADO cai no filtro legado por papel, idêntico ao de antes do
 *    GR-637. Sem isso o menu ficaria VAZIO para todo usuário que a migração
 *    ainda não alcançou — o token dele não tem client role nenhuma
 *    (GR-561, regra 16: bloquear só depois da conferência da migração).
 *
 * Sem `access` o comportamento é o legado puro.
 */
export const filterNavigationGroups = (
  role?: PermissionRole | null,
  access?: NavigationAccess,
): NavigationGroup[] => {
  const isAllowed = (item: NavigationItem) => {
    if (access?.isInternal) return true;

    if (access?.isMigrated) {
      const granted = access.permissions ?? [];
      return item.permissions.some((p) => granted.includes(p));
    }

    return role ? item.roles.includes(role) : FALLBACK_ITEM_IDS.includes(item.id);
  };

  return navigationGroups
    .map((group) => ({ ...group, items: group.items.filter(isAllowed) }))
    .filter((group) => group.items.length > 0);
};

/**
 * Papéis que liberavam a rota no regime LEGADO, lidos do próprio item de menu.
 *
 * Existe para a trava de rota não discordar do menu: enquanto o usuário não é
 * migrado, os dois passam a aplicar exatamente o mesmo critério, e a lista de
 * papéis continua declarada num lugar só.
 *
 * `undefined` para rota sem item de menu — nesse caso o regime legado não
 * restringia nada, e continuar liberando é o que preserva o acesso de hoje.
 */
export const getLegacyRolesForRoute = (
  path: string,
): PermissionRole[] | undefined =>
  navigationGroups
    .flatMap((group) => group.items)
    .find((item) => item.id === path)?.roles;

/**
 * Rotas do grupo `default` que não têm item no menu lateral, em ordem de
 * preferência. Servem de pouso quando o menu inteiro está vazio — situação
 * real para quem só tem perfis sem tela (`enterprise`, `admin`).
 */
const LANDING_FALLBACKS: ReadonlyArray<{ path: string; permission: Permission }> = [
  { path: "/meu-perfil", permission: PERMISSIONS.EDIT_OWN_PROFILE },
  { path: "/ajuda", permission: PERMISSIONS.VIEW_HELP_CENTER },
  { path: "/suporte", permission: PERMISSIONS.VIEW_SUPPORT },
];

/**
 * Primeira rota que o usuário consegue abrir — destino de todo pouso
 * pós-login e de todo botão "voltar ao início".
 *
 * Cinco pontos do app cravavam `/dashboard`. Com a trava de rota ligada, quem
 * não tem `view-dashboard` cairia em acesso negado logo depois de logar, o que
 * lê como login quebrado. Aqui o destino sai do MESMO filtro que monta o menu,
 * então pouso e menu nunca discordam.
 *
 * `/dashboard` continua sendo o padrão para interno e para quem não foi
 * migrado — nesses casos `filterNavigationGroups` devolve o menu cheio.
 */
export const getLandingRoute = (
  role?: PermissionRole | null,
  access?: NavigationAccess,
): string => {
  const primeiroDoMenu = filterNavigationGroups(role, access)[0]?.items[0]?.id;
  if (primeiroDoMenu) return primeiroDoMenu;

  // Menu vazio: sobra o que o grupo `default` dá sem aparecer no menu.
  if (access?.isInternal || !access?.isMigrated) return "/meu-perfil";
  const granted = access.permissions ?? [];
  const alternativa = LANDING_FALLBACKS.find((r) => granted.includes(r.permission));
  return alternativa?.path ?? "/meu-perfil";
};

/**
 * Item ativo por rota exata, com dois ajustes: `/` também cobre `/dashboard`
 * (destino do redirect pós-login) e sub-rotas acendem o item pai.
 */
export const isItemActive = (itemId: string, pathname?: string): boolean => {
  if (!pathname) return false;
  if (pathname === itemId) return true;
  if (itemId === "/") return pathname === "/dashboard";
  return pathname.startsWith(`${itemId}/`);
};
